<template>
  <two-factor-layout v-if="challenge" title="Verificación en dos pasos" :subtitle="subtitle">

    <two-factor-code-input ref="codeInput" :disabled="loading" @input="code = $event" @complete="verify"/>

    <p v-if="error" class="two-factor-verify__error">{{ error }}</p>

    <vs-button
      class="w-full mt-5"
      color="primary"
      type="relief"
      :disabled="loading || code.length !== 6"
      @click="verify">Verificar</vs-button>

    <p class="two-factor-verify__muted text-center mt-4">
      <template v-if="emailSent">
        <span v-if="resendIn > 0">Puedes pedir otro código en {{ resendIn }} s</span>
        <a v-else href="#" @click.prevent="sendEmailCode">Reenviar código</a>
      </template>
      <a v-else href="#" @click.prevent="sendEmailCode">¿No tienes tu celular a la mano? Recibir el código por correo</a>
    </p>
  </two-factor-layout>
</template>

<script>
import { twoFactorChallenge } from '@/_helpers';
import { twoFactorService } from '@/_services';
import TwoFactorLayout from './components/TwoFactorLayout';
import TwoFactorCodeInput from './components/TwoFactorCodeInput';

const RESEND_SECONDS = 60;

// Segundo paso del login para quien ya tiene método: el código de su app o el que le
// enviamos por correo. Quien usa app puede pedir el código por correo como alternativa.
export default {
  name: 'TwoFactorVerify',
  components: { TwoFactorLayout, TwoFactorCodeInput },
  data() {
    return {
      challenge: null,
      code: '',
      loading: false,
      error: null,
      emailSent: false,
      resendIn: 0,
      timer: null
    }
  },
  computed: {
    subtitle() {
      if (this.challenge.method === 'TOTP' && !this.emailSent) {
        return 'Abre tu app autenticadora y escribe el código de 6 dígitos de Verificamex Firma.';
      }
      if (this.challenge.method === 'TOTP') {
        return `Escribe el código de tu app o el que enviamos a ${this.challenge.email_hint}.`;
      }
      return `Escribe el código de 6 dígitos que enviamos a ${this.challenge.email_hint}.`;
    }
  },
  created() {
    const challenge = twoFactorChallenge.get();
    if (!challenge || challenge.status !== 'VERIFY') {
      this.$router.replace('/login');
      return;
    }
    this.challenge = challenge;
    // Con correo, el código salió junto con la respuesta del login.
    if (challenge.method === 'EMAIL') {
      this.emailSent = true;
      this.startResendTimer();
    }
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    verify() {
      if (this.loading || this.code.length !== 6) {
        return;
      }
      this.loading = true;
      this.error = null;
      twoFactorService.verifyLogin(this.challenge.token, this.code, this.challenge.remember)
        .then(authResponse => {
          return this.$store.dispatch('account/twoFactorComplete', { authResponse, aclChangeRole: this.$acl.change });
        }, error => {
          this.handleError(error);
          this.$refs.codeInput.clear();
        })
        // twoFactorComplete ya muestra su propio error si falla al cargar el perfil.
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    sendEmailCode() {
      this.error = null;
      twoFactorService.sendLoginEmailCode(this.challenge.token)
        .then(() => {
          this.emailSent = true;
          this.startResendTimer();
          this.$vs.notify({ title: 'Código enviado', text: `Revisa tu correo ${this.challenge.email_hint}`, color: 'success', iconPack: 'feather', icon: 'icon-mail' });
        }, this.handleError);
    },
    startResendTimer() {
      this.resendIn = RESEND_SECONDS;
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        this.resendIn--;
        if (this.resendIn <= 0) {
          clearInterval(this.timer);
        }
      }, 1000);
    },
    handleError(error) {
      // 401: el token de verificación expiró (10 min). Hay que volver a poner la contraseña.
      if (error && error.status === 401) {
        twoFactorChallenge.clear();
        this.$store.dispatch('alert/error', error.message);
        this.$router.replace('/login');
        return;
      }
      this.error = (error && error.message) || error;
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/vuexy/_colorsVariables.scss';

.two-factor-verify__error {
  margin-top: 12px;
  text-align: center;
  font-size: 0.9rem;
  color: $danger;
}

.two-factor-verify__muted {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.55);

  a { color: $primary; font-weight: 500; }
}
</style>
